import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { clearSession, tokenExpiresAt, SESSION_CHANGED, SESSION_EXPIRED } from '../services/session';
import { navigate } from '../services/navigation';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const expiry = tokenExpiresAt(sessionStorage.getItem('token') || '');
    if (expiry === null || expiry <= Date.now()) { clearSession(); return null; }
    const raw = sessionStorage.getItem("user");
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (err) {
      console.error("AuthProvider: failed parsing saved user", err);
      sessionStorage.removeItem("user");
      return null;
    }
  });

  const [isLoading, setIsLoading] = useState(false);

  const logout = useCallback(() => {
    clearSession();
    setUser(null);
  }, []);

  useEffect(() => {
    let timer;
    const expired = () => {
      logout();
      navigate('/signin');
    };
    const check = () => {
      clearTimeout(timer);
      const token = sessionStorage.getItem('token');
      if (!token) { if (user) expired(); return; }
      const expiry = tokenExpiresAt(token);
      if (expiry === null || expiry <= Date.now()) { expired(); return; }
      // Browsers cap timeout durations and throttle timers in background tabs.
      timer = setTimeout(check, Math.min(expiry - Date.now(), 2147483647));
    };
    const visible = () => { if (document.visibilityState === 'visible') check(); };
    check();
    window.addEventListener(SESSION_CHANGED, check);
    window.addEventListener(SESSION_EXPIRED, expired);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', visible);
    return () => {
      clearTimeout(timer);
      window.removeEventListener(SESSION_CHANGED, check);
      window.removeEventListener(SESSION_EXPIRED, expired);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [user, logout]);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        logout,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
